<script lang="ts" setup>
import { CreateBotMutationDocument } from './CreateBotModal.generated'

const open = defineModel<boolean>({ required: true })

const state = reactive({
  name: '',
  description: '',
  teamIds: [] as string[],
})

const createdBotKey = ref('')
const { copy: copyToClipboard, copied: keyCopied } = useClipboard({ legacy: true })
const createdBotSlug = ref<string | null>(null)
const showKeyModal = ref(false)

const { execute: createBot, isFetching: isCreating } = useMutation(CreateBotMutationDocument, {
  refetchTags: ['bots'],
})

watch(open, (newOpen) => {
  if (!newOpen) return
  state.name = ''
  state.description = ''
  state.teamIds = []
})

async function save() {
  const { data } = await createBot({
    input: {
      name: state.name,
      description: state.description,
      teamIds: state.teamIds.length > 0 ? state.teamIds : undefined,
    },
  })

  const result = data?.createBot
  if (result?.botKey) {
    createdBotKey.value = result.botKey
    createdBotSlug.value = result.bot?.slug ?? null
    open.value = false
    showKeyModal.value = true
  }
}

function closeKeyModal() {
  showKeyModal.value = false
  createdBotKey.value = ''
  if (createdBotSlug.value) {
    navigateTo(`/bots/${createdBotSlug.value}`)
  }
}
</script>

<template>
  <!-- Create form modal -->
  <UModal v-model="open">
    <UCard class="bg-white">
      <UButton icon="light:xmark" variant="ghost" color="gray" class="absolute right-2 top-2" @click="open = false" />

      <div class="text-lg font-semibold">Create Bot</div>

      <div class="mt-4 space-y-4">
        <FormGroup label="Name" required size="md">
          <UInput v-model="state.name" placeholder="e.g. Python Backend Bot" autofocus />
        </FormGroup>
        <FormGroup label="Description" size="md">
          <UTextarea v-model="state.description" placeholder="What does this bot do?" :rows="2" />
        </FormGroup>
        <FormGroup label="Teams" size="md">
          <TeamsSelector v-model="state.teamIds" />
        </FormGroup>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <UButton size="sm" variant="ghost" color="gray" @click="open = false"> Cancel </UButton>
          <UButton size="sm" :loading="isCreating" :disabled="!state.name.trim()" @click="save"> Create </UButton>
        </div>
      </template>
    </UCard>
  </UModal>

  <!-- Bot Key disclosure modal (matches existing token pattern) -->
  <UModal v-model="showKeyModal">
    <UCard>
      <template #header>
        <h3 class="text-lg font-semibold">Bot created successfully</h3>
      </template>

      <div class="space-y-4">
        <div class="rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
          Make sure you copy it now. You won't be able to see it again!
        </div>

        <div class="space-y-2">
          <div class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Use this API key to authenticate your bot directly:
          </div>
          <code class="block rounded-lg bg-gray-100 p-3 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >Authorization: Bearer &lt;key&gt;</code
          >
        </div>

        <div class="relative">
          <div
            class="rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-sm dark:border-gray-700 dark:bg-gray-800"
          >
            {{ createdBotKey }}
          </div>
          <UButton
            size="xs"
            :icon="keyCopied ? 'light:check' : 'light:copy'"
            :label="keyCopied ? 'Copied' : 'Copy'"
            variant="ghost"
            class="absolute right-2 top-2"
            @click="copyToClipboard(createdBotKey)"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end">
          <UButton label="Done" color="primary" @click="closeKeyModal" />
        </div>
      </template>
    </UCard>
  </UModal>
</template>
